import { ArrowDropDown, ArrowDropUp, Check } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, IconButton, Menu, MenuItem, Stack, TextField, Tooltip, Typography, useTheme } from "@mui/material";
import {
  type ColumnDef,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { format } from "date-fns";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import Locator from "@/locator";
import { removeMember, updateMemberRole } from "@/models/actions/clientActions";
import { DataContext } from "@/models/DataProvider";
import { useReactiveSelectQuery } from "@/models/hooks/useReactiveSelectQuery.ts";
import { workspaceInviteTableSelect } from "@/models/repositories";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import { TrackingContext } from "@/models/TrackingStateProvider";
import { UserManagementContext } from "@/models/UserManagementContextProvider";
import DataTable from "../DataTable";
import Loading from "../Loading/Index";

function formatDate(date: string) {
  if (!date) return "";
  return format(new Date(date), "MMM dd, yyyy");
}

export default function WorkspaceInvitesTable({ newDesign }: { newDesign?: boolean }) {
  const { currentWorkspaceId } = useContext(WorkspaceContext);
  const workspaceId = currentWorkspaceId;
  const { ampli } = useContext(TrackingContext);
  const { availableWorkspaceRoles, getWorkspaceRoleLabel } = useContext(DataContext);
  const { activeSearchField, searchFilter, setSearchFilter } = useContext(UserManagementContext);
  const theme = useTheme();
  const [revokingId, setRevokingId] = useState<string>("");
  const [roleMenuEl, setRoleMenuEl] = useState<null | HTMLElement>(null);
  const [roleMenuOpen, setRoleMenuOpen] = useState<string>();
  const [updatingMemberId, setUpdatingMemberId] = useState<string>();
  const roleOptions = Array?.from(availableWorkspaceRoles?.values())?.filter((r) => r?.enabled);

  const closeRoleMenu = useCallback(() => {
    setRoleMenuEl(null);
    setRoleMenuOpen("");
  }, []);

  useEffect(() => {
    if (roleMenuEl && !roleMenuEl.isConnected) {
      closeRoleMenu();
    }
  }, [roleMenuEl, closeRoleMenu]);

  const workspaceInvites =
    useReactiveSelectQuery(
      workspaceInviteTableSelect,
      [{ workspaceId }],
      "WorkspaceInvitesTable:workspaceInviteTableSelect",
    ) ?? [];

  const updateWorkspaceMemberRole = useCallback(
    async (workspaceId, membershipId, currentRole, newRole) => {
      try {
        setUpdatingMemberId(membershipId);
        if (currentRole === "admin" && (newRole === "member" || newRole === "limitedMember")) {
          ampli.downgradePermissionMember();
        }
        if ((currentRole === "member" || currentRole === "limitedMember") && newRole === "admin") {
          ampli.upgradePermissionAdmin();
        }
        await updateMemberRole(workspaceId, [membershipId], newRole);
      } catch (e) {
        console.log(e);
      }
      setUpdatingMemberId("");
    },
    [updateMemberRole],
  );

  const currentInvite = useMemo(
    () => workspaceInvites.find((inv) => inv.workspaceMembershipId === roleMenuOpen),
    [workspaceInvites, roleMenuOpen],
  );

  const revokeInvite = useCallback(
    async (workspaceMembershipId: string) => {
      try {
        setRevokingId(workspaceMembershipId);
        if (workspaceId) {
          ampli.revokePendingInvite();
          await removeMember(workspaceId, [workspaceMembershipId]);
        }
      } catch (e) {
        console.error("Error removing invite", e);
      }
      setRevokingId("");
    },
    [ampli, workspaceId, removeMember],
  );

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        id: "contact",
        accessorFn: (row) => row.email || row.phoneNumber || row.id,
        header: "Contact",
        minSize: 200,
        meta: { flex: 1 },
        cell: ({ row }) => {
          const contact = row.original.email || row.original.phoneNumber || row.original.id;
          return (
            <Tooltip title={contact}>
              <Typography fontWeight={700}>{contact}</Typography>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "name",
        header: "Name",
        minSize: 200,
        meta: { flex: 1 },
        cell: ({ row }) => <Typography fontWeight={700}>{row.original.name}</Typography>,
      },
      {
        id: "date",
        accessorFn: (row) => row.date,
        header: "Invitation Date",
        minSize: 200,
        enableGlobalFilter: false,
        sortingFn: "datetime",
        meta: { flex: 1 },
        cell: ({ getValue }) => formatDate(getValue() as string),
      },
      {
        accessorKey: "role",
        header: "Type",
        minSize: 120,
        maxSize: 200,
        meta: { flex: 1 },
        enableGlobalFilter: false,
        cell: ({ row }) => {
          const workspaceMembershipId = row.original.workspaceMembershipId ?? null;
          const memberTestId = row.original.email || row.original.phoneNumber;
          return (
            <Box>
              {row.original.mine ? (
                <Typography sx={{ textTransform: "capitalize", pr: 4.2, pl: 1 }}>
                  {getWorkspaceRoleLabel(row.original.role) || row.original.role}
                </Typography>
              ) : (
                <IconButton
                  onClick={(e) => {
                    setRoleMenuEl(e.currentTarget);
                    setRoleMenuOpen(workspaceMembershipId);
                  }}
                  aria-label={Locator.workspaceNav.members.activeMembers.roleSelect}
                  data-testid={Locator.workspaceNav.members.activeMembers.roleSelectTestId(memberTestId)}
                  sx={{
                    textTransform: "capitalize",
                    background: "transparent",
                  }}
                >
                  <Typography>{getWorkspaceRoleLabel(row.original.role) || row.original.role}</Typography>
                  {workspaceMembershipId === roleMenuOpen ? <ArrowDropUp role="img" /> : <ArrowDropDown role="img" />}
                </IconButton>
              )}
            </Box>
          );
        },
      },
      {
        id: "revoke",
        header: "",
        size: 115,
        enableSorting: false,
        enableGlobalFilter: false,
        meta: { align: "right" as const },
        cell: ({ row }) => {
          if (!row.original.mine) {
            const pendingInviteId = row.original.email || row.original.phoneNumber;
            return (
              <LoadingButton
                loading={revokingId === row.original.workspaceMembershipId}
                onClick={() => revokeInvite(row.original.workspaceMembershipId)}
                aria-label={Locator.workspaceNav.members.pendingInvites.revokeButton}
                data-testid={Locator.workspaceNav.members.pendingInvites.revokeTestId(pendingInviteId)}
                sx={{
                  padding: "8px 12px",
                  fontSize: "0.875rem",
                  background: theme.palette.secondary.dark,
                  maxWidth: "85px",
                  lineHeight: 1,
                }}
              >
                REVOKE
              </LoadingButton>
            );
          }
          return null;
        },
      },
    ],
    [getWorkspaceRoleLabel, roleMenuOpen, revokingId, theme, revokeInvite],
  );

  const table = useReactTable({
    data: workspaceInvites,
    columns,
    state: { globalFilter: searchFilter },
    onGlobalFilterChange: setSearchFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 20 } },
  });

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: newDesign ? "auto" : 1300,
        paddingBottom: "100px",
        overflow: "hidden",
        height: "calc(100vh - 90px)",
      }}
    >
      {workspaceInvites?.length > 0 ? (
        <DataTable
          table={table}
          pageSizeOptions={[20, 60, 100]}
          toolbar={
            <TextField
              inputRef={activeSearchField}
              placeholder="Search for members"
              value={searchFilter ?? ""}
              onChange={(e) => setSearchFilter(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              sx={{ mb: 1 }}
            />
          }
        />
      ) : (
        <Stack
          sx={{
            width: "100%",
            py: 1.5,
            px: 2,
            background: theme.palette.secondary.main,
            borderRadius: 1.5,
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>No pending invites</Typography>
        </Stack>
      )}
      {roleMenuEl?.isConnected && (
        <Menu
          anchorEl={roleMenuEl}
          open={Boolean(roleMenuOpen)}
          onClose={closeRoleMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          sx={{ mt: 1, ml: 1 }}
          slotProps={{ paper: { sx: { width: 185, px: 1, py: 1.5 } } }}
        >
          {roleOptions?.map((r) => (
            <MenuItem
              sx={{
                maxHeight: 40,
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: "4px",
                background: currentInvite?.role === r?.role ? theme.palette.secondary.main : "transparent",
                border: currentInvite?.role === r?.role ? "1.5px solid" : "none",
                borderColor: currentInvite?.role === r?.role ? theme.palette.secondary.light : "none",
                fontWeight: 500,
                textTransform: "capitalize",
              }}
              key={`role-option-${r?.role}`}
              onClick={() => {
                updateWorkspaceMemberRole(workspaceId, roleMenuOpen, currentInvite?.role, r?.role);
                closeRoleMenu();
              }}
              disabled={roleMenuOpen === updatingMemberId}
            >
              <Stack sx={{ position: "relative", maxWidth: 24, alignItems: "center" }}>
                {currentInvite?.role === r?.role ? <Check /> : <Box sx={{ width: 24 }} />}
                {currentInvite?.role !== r?.role && roleMenuOpen === updatingMemberId ? (
                  <Loading variant="disabled" size="tiny" />
                ) : null}
              </Stack>
              {r?.label || r?.role}
            </MenuItem>
          ))}
        </Menu>
      )}
    </Box>
  );
}
